import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Snackbar,
    Grid,
    Link,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

import { LayoutPaper, DesktopLayout } from 'src/layout';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import SubscriptionsService from 'src/services/SubscriptionsService';
import { ROUTES_URL } from 'src/constants/routes';
import Copy from 'src/icons/Copy';
import UserPlus from 'src/icons/UserPlus';
import Title from 'src/components/Title';
import TelegramLogo from 'src/icons/TelegramLogo';

// CSS анимации
const styles = `
  @keyframes glow {
    0% { box-shadow: 0 0 5px rgba(255, 224, 102, 0.5); }
    50% { box-shadow: 0 0 20px rgba(255, 224, 102, 0.8); }
    100% { box-shadow: 0 0 5px rgba(255, 224, 102, 0.5); }
  }
  
  @keyframes rewardGlow {
    0% { box-shadow: 0 0 5px rgba(74, 226, 135, 0.3); }
    50% { box-shadow: 0 0 20px rgba(74, 226, 135, 0.6); }
    100% { box-shadow: 0 0 5px rgba(74, 226, 135, 0.3); }
  }
`;

// Добавляем стили в head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement('style');
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
}

const Partnership = () => {
    const { enqueueSnackbar } = useSnackbar();
    console.log('Partnership component rendered');

    const isMobile = useIsMobileSize();
    const [promocode, setPromocode] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState('');

    // Загрузка моего промокода
    const {
        data: myPromocode,
        isLoading: isLoadingPromocode,
        refetch: refetchPromocode,
    } = useQuery('myPromocode', () => SubscriptionsService.getMyPromocode(), {
        onSuccess: (data) => {
            setPromocode(data.code);
        },
        onError: (error) => {
            enqueueSnackbar('Ошибка загрузки промокода', { variant: 'error' });
        },
    });

    // Загрузка моих рефералов
    const { data: referrals, isLoading: isLoadingReferrals } = useQuery(
        'referrals',
        () => SubscriptionsService.getMyReferrals(),
        {
            onError: (error) => {
                enqueueSnackbar('Ошибка загрузки рефералов', { variant: 'error' });
            },
        }
    );

    const handleCopyPromocode = async () => {
        try {
            await navigator.clipboard.writeText(myPromocode?.code || '');
            enqueueSnackbar('Промокод скопирован', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Ошибка копирования', { variant: 'error' });
        }
    };

    const handleEditPromocode = () => {
        setIsEditing(true);
        setValidationError('');
    };

    const handleSavePromocode = async () => {
        if (!promocode.trim()) {
            setValidationError('Промокод не может быть пустым');
            return;
        }

        // Проверяем формат промокода (только буквы и цифры)
        const promoCodeRegex = /^[A-Z0-9]+$/;
        const upperCasePromocode = promocode.toUpperCase();

        if (!promoCodeRegex.test(upperCasePromocode)) {
            setValidationError('Промокод может содержать только буквы и цифры');
            return;
        }

        setIsValidating(true);
        setValidationError('');

        console.log('Проверяем промокод:', upperCasePromocode);

        try {
            // Сначала проверяем, что промокод не занят
            await SubscriptionsService.checkPromocode(upperCasePromocode);

            // Если промокод валидный (существует), значит он занят
            console.log('Промокод занят!');
            setValidationError('Промокод уже занят');
        } catch (error: any) {
            console.error('Ошибка валидации:', error);

            // Если промокод недействительный (не существует), значит он свободен
            if (error?.error === 'Недействительный промокод') {
                console.log('Промокод свободен, сохраняем...');
                try {
                    await SubscriptionsService.updateMyPromocode({ code: upperCasePromocode });

                    setIsEditing(false);
                    refetchPromocode();
                    enqueueSnackbar('Промокод успешно изменен', { variant: 'success' });
                } catch (saveError: any) {
                    console.error('Ошибка сохранения:', saveError);
                    setValidationError(
                        saveError?.response?.data?.error || saveError?.error || 'Ошибка сохранения промокода'
                    );
                }
            } else {
                setValidationError(error?.error || 'Ошибка проверки промокода');
            }
        } finally {
            setIsValidating(false);
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setPromocode(myPromocode?.code || '');
        setValidationError('');
    };

    const renderPromocodeSection = () => (
        <Box
            p={2}
            bgcolor="#FFF9E6"
            borderRadius={3}
            boxShadow={0}
            display="flex"
            flexDirection="column"
            gap={1}
            height="100%"
        >
            <Typography
                variant="h3"
                color="primary"
                sx={{ fontWeight: 600, mb: 1 }}
            >
                Мой промокод
            </Typography>

            {isEditing ? (
                <Box>
                    <TextField
                        fullWidth
                        value={promocode}
                        onChange={(e) => setPromocode(e.target.value.toUpperCase())}
                        placeholder="Введите промокод"
                        error={!!validationError}
                        helperText={validationError}
                        sx={{ mb: 2 }}
                        inputProps={{
                            style: { textTransform: 'uppercase' },
                        }}
                    />
                    <Box
                        display="flex"
                        gap={1}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSavePromocode}
                            disabled={isValidating}
                        >
                            {isValidating ? 'Проверка...' : 'Сохранить'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCancelEdit}
                            disabled={isValidating}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={2}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                        >
                            <Typography
                                variant="h4"
                                sx={{ fontFamily: 'monospace', flex: 1 }}
                            >
                                {myPromocode?.code || 'Нет промокода'}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleEditPromocode}
                                sx={{
                                    '&:hover': {
                                        opacity: 0.7,
                                    },
                                }}
                            >
                                Изменить
                            </Button>
                        </Box>

                        <Box
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleCopyPromocode}
                                startIcon={<Copy />}
                                sx={{
                                    borderColor: '#1F1F1F',
                                    color: '#1F1F1F',
                                    '&:hover': {
                                        borderColor: '#000000',
                                        backgroundColor: 'rgba(31, 31, 31, 0.1)',
                                    },
                                }}
                            >
                                Скопировать
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    const message = `Хочешь находить клиентов в Telegram?\n\nИспользуй мой промокод ${
                                        myPromocode?.code || 'MONEYGOLD1'
                                    } на tghunter.ru - получишь ${
                                        myPromocode?.discount_percent || 15
                                    }% скидку на первую покупку!\n\nЯ уже пользуюсь, работает 🔥`;
                                    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(
                                        'https://tghunter.ru'
                                    )}&text=${encodeURIComponent(message)}`;
                                    window.open(telegramUrl, '_blank');
                                }}
                                startIcon={<TelegramLogo color="#0088CC" />}
                                sx={{
                                    borderColor: '#0088CC',
                                    color: '#0088CC',
                                    '&:hover': {
                                        borderColor: '#006699',
                                        backgroundColor: 'rgba(0, 136, 204, 0.1)',
                                    },
                                }}
                            >
                                Поделиться в Telegram
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );

    const renderReferralsCountSection = () => (
        <Box
            p={2}
            bgcolor="#E3F2FD"
            borderRadius={3}
            boxShadow={0}
            display="flex"
            flexDirection="column"
            gap={1}
            height="100%"
        >
            <Typography
                variant="h4"
                color="primary"
                sx={{ fontWeight: 500, mb: 1 }}
            >
                👥 Количество рефералов
            </Typography>
            <Typography
                variant="h2"
                sx={{
                    fontWeight: 700,
                    color: '#0044FF',
                    fontSize: '2.5rem',
                }}
            >
                {referrals?.length || 0}
            </Typography>
        </Box>
    );

    const renderRewardsSection = () => (
        <Box
            p={2}
            bgcolor="#E8F5E8"
            borderRadius={3}
            boxShadow={0}
            display="flex"
            flexDirection="column"
            gap={1}
            height="100%"
        >
            <Typography
                variant="h4"
                color="primary"
                sx={{ fontWeight: 500, mb: 1 }}
            >
                💰 Сумма наград
            </Typography>
            <Typography
                variant="h2"
                sx={{
                    fontWeight: 700,
                    color: '#4AE287',
                    fontSize: '2.5rem',
                }}
            >
                {Math.round(referrals?.reduce((sum, ref) => sum + ref.earned, 0) || 0)} Б
            </Typography>
            <Typography
                variant="caption"
                sx={{
                    color: '#9FA9BC',
                    fontSize: '0.75rem',
                }}
            >
                (1 Б = 1 ₽)
            </Typography>
        </Box>
    );

    const renderReferralsSection = () => (
        <Box
            p={2}
            bgcolor="#FFFFFF"
            borderRadius={3}
            boxShadow={0}
            display="flex"
            flexDirection="column"
            gap={1}
        >
            <Typography
                variant="h3"
                color="primary"
                sx={{ fontWeight: 600, mb: 1 }}
            >
                Кого вы уже пригласили
            </Typography>

            {referrals && referrals.length > 0 ? (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Начислено</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {referrals.map((referral, index) => (
                                <TableRow key={index}>
                                    <TableCell>{referral.email}</TableCell>
                                    <TableCell>{Math.round(referral.earned)} Б</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    У вас пока нет рефералов
                </Typography>
            )}
        </Box>
    );

    const renderContent = () => (
        <LoadingWrapper isLoading={isLoadingPromocode || isLoadingReferrals}>
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box mb={1.5}>
                    <Title>Партнёрство</Title>
                </Box>
                <Box
                    maxWidth="80%"
                    mb={20 / 8}
                >
                    <Typography
                        variant="body1"
                        color="primary"
                    >
                        Делитесь своим промокодом и получайте вознаграждение
                    </Typography>
                </Box>
                <Box
                    mb={2}
                    p={2}
                    bgcolor="#fff"
                    borderRadius={3}
                    boxShadow={0}
                    display="flex"
                    flexDirection="column"
                    gap={1}
                >
                    <Typography
                        variant="body1"
                        sx={{ color: '#000000', lineHeight: 1.6, mb: 1 }}
                    >
                        🎁 Партнер получит <strong>{myPromocode?.discount_percent || 15}% скидку</strong> на первую
                        покупку
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: '#000000', lineHeight: 1.6, mb: 2 }}
                    >
                        💸 Вы будете получать <strong>{myPromocode?.reward_percent || 15}% с каждой оплаты</strong>{' '}
                        партнера
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ mt: 1 }}
                    >
                        <Link
                            component="a"
                            href="https://tghunter.ru/invitefriends"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: '#0044FF',
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Правила акции «Пригласить друга»
                        </Link>
                    </Typography>
                </Box>
                <Grid
                    container
                    spacing={2}
                    sx={{ mb: 2 }}
                >
                    <Grid
                        item
                        xs={12}
                        sm={4}
                    >
                        {renderPromocodeSection()}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={4}
                    >
                        {renderReferralsCountSection()}
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={4}
                    >
                        {renderRewardsSection()}
                    </Grid>
                </Grid>
                {renderReferralsSection()}
            </Box>
        </LoadingWrapper>
    );

    return isMobile ? <LayoutPaper>{renderContent()}</LayoutPaper> : <DesktopLayout>{renderContent()}</DesktopLayout>;
};

export default Partnership;
